import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * Light or dark, for the whole app.
 *
 * Three states, not two. Until you touch the control the app follows the
 * operating system, so someone who runs their machine dark opens the
 * dashboard dark without being asked; once you choose, your choice sticks
 * and stops tracking the system. That is the behaviour people already expect
 * from every other app on the machine, and the reason `preference` (null =
 * follow the system) is stored separately from `mode` (what is on screen).
 *
 * Kept out of the theme provider because the mode outlives the theme: it is
 * read by CSS that cannot see MUI at all (the toast skin) and by the document
 * element, both of which need it before any brand has been resolved.
 */

const STORAGE_KEY = 'techproone:color-mode';

// localStorage throws outright in a private window and in some embedded
// webviews, and the app must not fail to boot over a display preference.
const readStored = () => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch (_) {
    return null;
  }
};

const writeStored = (v) => {
  try {
    if (v) window.localStorage.setItem(STORAGE_KEY, v);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    // A preference we cannot persist is still a preference for this session.
  }
};

// jsdom has no matchMedia, so the tests render through here with none.
const darkQuery = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  ? window.matchMedia('(prefers-color-scheme: dark)')
  : null);

const ColorModeContext = createContext({
  mode: 'light',
  preference: null,
  setPreference: () => {},
  toggle: () => {},
});

export function ColorModeProvider({ children }) {
  const [preference, setPreferenceState] = useState(readStored);
  const [systemDark, setSystemDark] = useState(() => Boolean(darkQuery()?.matches));

  useEffect(() => {
    const q = darkQuery();
    if (!q) return undefined;
    const onChange = (e) => setSystemDark(e.matches);
    // Safari before 14 has no addEventListener on a MediaQueryList.
    if (q.addEventListener) {
      q.addEventListener('change', onChange);
      return () => q.removeEventListener('change', onChange);
    }
    q.addListener(onChange);
    return () => q.removeListener(onChange);
  }, []);

  const mode = preference || (systemDark ? 'dark' : 'light');

  const setPreference = useCallback((next) => {
    writeStored(next);
    setPreferenceState(next);
  }, []);

  const toggle = useCallback(
    () => setPreference(mode === 'dark' ? 'light' : 'dark'),
    [mode, setPreference],
  );

  // For the stylesheets MUI cannot reach — react-toastify is skinned by class
  // name from its own CSS file, so it follows this attribute instead.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.colorMode = mode;
    }
  }, [mode]);

  const value = useMemo(
    () => ({ mode, preference, setPreference, toggle }),
    [mode, preference, setPreference, toggle],
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
}

export const useColorMode = () => useContext(ColorModeContext);

export default ColorModeContext;
